/**
 * Customer Onboarding page (Phase 5)
 *
 * Three-step wizard: select target IT Glue org → upload PDF → preview proposed
 * records → confirm and write. The dashboard owns the final write (calls
 * bulk_create directly with dry_run=false) so the user has full control over
 * what lands in IT Glue — the agent's role is purely "markdown → structured
 * JSON proposal".
 *
 * State machine:
 *   idle → extracting → analysing → preview_ready → writing → completed
 *                                          ↓
 *                                      (back to idle on reset)
 *   any state → failed (on error)
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  Row, Col, Card, Typography, Select, Upload, Button, Table, Tag, Alert,
  Spin, Progress, Empty, Space, Steps, message, Divider, Input,
} from 'antd';
import {
  FileAddOutlined, InboxOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  ReloadOutlined, ApiOutlined, FolderOpenOutlined, WarningOutlined,
  DownloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import {
  extractPdfMarkdown, prepareOnboarding, runExtractJob,
  commitOnboarding, listItglueOrganizations,
  generateTemplate, parseFilledTemplate, downloadBase64Pdf, fileToBase64,
} from '../services/api/onboarding';
import OnboardingWizard from '../features/onboarding/OnboardingWizard.jsx';
import ReuseBanner from '../features/onboarding/ReuseBanner.jsx';
import ResultsStep from '../features/onboarding/steps/ResultsStep.jsx';
import { summariseExtractProgress } from '../features/onboarding/extractProgress';
import { safeHref } from '../utils/safeUrl';

const { TextArea } = Input;

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

// ─── Styling (colours come from the root ConfigProvider + --odin-* tokens) ──

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 16px;
`;

const StatusLine = styled.div`
  color: var(--odin-text-secondary);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Tool name → friendly display label for the proposal preview cards
const TOOL_LABELS = {
  create_location: 'Location',
  create_configuration: 'Configuration',
  create_contact: 'Contact',
  create_password: 'Password (vaulted)',
  create_flexible_asset: 'Flex-asset',
  create_password_folder: 'Password folder',
  create_document_folder: 'Document folder',
  upload_attachment: 'Attachment',
  update_organization: 'Quick Notes update',
};

// ─── Main component ────────────────────────────────────────────────────────

function CustomerOnboardingPage() {
  const { user } = useAuth();
  const userId = user?.localAccountId || user?.homeAccountId || 'anonymous';

  // Wizard state
  const [status, setStatus] = useState('idle');
  // idle|extracting|preparing|analysing|preview_ready|writing|completed|failed
  const [error, setError] = useState(null);

  // Org selector
  const [orgQuery, setOrgQuery] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Extracted PDF + uploaded file metadata for the prepare call
  const [extracted, setExtracted] = useState(null);
  const [pdfMeta, setPdfMeta] = useState(null);  // {filename, sizeBytes}

  // Phase O4 orchestrator state — pipelineId + cached folder IDs map
  const [pipelineId, setPipelineId] = useState(null);
  const [folderIds, setFolderIds] = useState(null);
  const [prepareSteps, setPrepareSteps] = useState([]);
  const [prepareFollowUps, setPrepareFollowUps] = useState([]);

  // Agent proposal — new Phase O3 shape: {summary, quickNotes,
  // prerequisites[], dependents[], blockerWarnings[], duplicateWarnings[],
  // proposalNotes[]}
  const [proposal, setProposal] = useState(null);
  const [rawAgentText, setRawAgentText] = useState('');

  // Extract-time context for the review wizard (Task 12): flex-asset type
  // schemas, per-item validation verdicts, and placeholder-password
  // follow-ups — all produced by /api/onboard/extract alongside `proposal`
  // and needed by OnboardingWizard's per-section pages, its passwords-step
  // FollowUpsPanel, and its Confirm step. Defaulted here (not in the API
  // client) so an older backend that omits these keys degrades gracefully.
  const [schemas, setSchemas] = useState({});
  const [validation, setValidation] = useState([]);
  const [extractFollowUps, setExtractFollowUps] = useState([]);

  // Visible scan reuse: ISO timestamp of the reused proposal's ORIGINAL
  // extraction on a cache hit (OnboardExtractResponse.cached_at); null on a
  // fresh (non-cached) run. Drives ReuseBanner — see handleRescan below for
  // how "Re-scan with AI" forces a fresh extraction.
  const [cachedAt, setCachedAt] = useState(null);

  // Editable Quick Notes (operator can revise before commit)
  const [quickNotesEdited, setQuickNotesEdited] = useState('');

  // Bumped whenever a wholesale-new proposal replaces the current one (a
  // fresh extraction, or a re-uploaded walk-round template) so
  // OnboardingWizard — which owns all tick-state internally — remounts
  // clean instead of reconciling stale per-section selections against a
  // different item set.
  const [wizardKey, setWizardKey] = useState(0);

  // Result of /api/onboard/commit
  const [writeResult, setWriteResult] = useState(null);

  // Live per-pass progress from runExtractJob's onProgress — {passes: {...}}.
  // null before the first poll lands / after extraction finishes.
  const [extractProgress, setExtractProgress] = useState(null);

  // Allow user to cancel an in-flight agent call
  const abortRef = useRef(null);

  // ─── Org search (debounced) ────────────────────────────────────────────
  useEffect(() => {
    if (!orgQuery || orgQuery.length < 2) {
      setOrgOptions([]);
      return;
    }
    let cancelled = false;
    setOrgLoading(true);
    const t = setTimeout(async () => {
      try {
        const result = await listItglueOrganizations(orgQuery);
        if (cancelled) return;
        // IT-Glue-MCP list_organizations returns { success, markdown, totalAvailable, returned }.
        // The `markdown` is structured: each org is `## <Name>\n- **ID**: <numeric>\n- ...`.
        // Parse it back into {id, name} pairs — much simpler than asking the MCP tool
        // for structured JSON (which would require a backend change).
        const md = result?.markdown || '';
        const orgs = [];
        // Split on level-2 headings; first chunk is the file preamble.
        const blocks = md.split(/^## /m).slice(1);
        for (const block of blocks) {
          const nameMatch = block.match(/^(.+?)$/m);
          const idMatch = block.match(/-\s*\*\*ID\*\*:\s*(\d+)/);
          if (nameMatch && idMatch) {
            orgs.push({ id: idMatch[1], name: nameMatch[1].trim() });
          }
        }
        setOrgOptions(
          orgs.map((o) => ({ value: o.id, label: o.name })).slice(0, 50),
        );
      } catch (err) {
        message.error(`Org search failed: ${err.message}`);
        setOrgOptions([]);
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [orgQuery]);

  // ─── Shared extraction call (first run + "Re-scan with AI") ─────────────
  // Both the initial Step 2 extraction (inside handleUpload below) and a
  // forced re-scan (handleRescan, fired from ReuseBanner — visible scan
  // reuse) hit the same /api/onboard/extract call and must go through
  // identical status transitions and error handling. Factored out so
  // handleRescan doesn't duplicate the analysing/preview_ready/failed state
  // machine. Callers always pass org/markdown/folderIds explicitly (never
  // reads them off state itself) so handleUpload can call it with values
  // that haven't landed in state yet (setExtracted/setFolderIds are async).
  const runExtraction = useCallback(
    async ({ orgId, orgName, markdown, folderIds: fids, forceRefresh = false }) => {
      setStatus('analysing');
      setError(null);
      setExtractProgress(null);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const extractResult = await runExtractJob({
          orgId,
          orgName,
          markdown,
          folderIds: fids,
          userId,
          forceRefresh,
          onProgress: setExtractProgress,
          signal: controller.signal,
        });
        abortRef.current = null;
        setExtractProgress(null);
        const prop = extractResult.proposal || {};
        setProposal(prop);
        setSchemas(extractResult.schemas || {});
        setValidation(extractResult.validation || []);
        setExtractFollowUps(extractResult.follow_ups || []);
        setRawAgentText(JSON.stringify(extractResult.pass_stats || [], null, 2));
        setQuickNotesEdited(prop.quickNotes || '');
        // Visible scan reuse: null on a fresh run, an ISO timestamp on a
        // cache hit — ReuseBanner renders only when this is truthy.
        setCachedAt(extractResult.cached_at || null);
        setWizardKey((k) => k + 1);
        setStatus('preview_ready');
      } catch (err) {
        setExtractProgress(null);
        if (err.name === 'AbortError') {
          setStatus('idle');
          return;
        }
        setError(err.message);
        setStatus('failed');
      }
    },
    [userId],
  );

  // ─── Step 1: PDF upload → extract → prepare → analyse ──────────────────
  const handleUpload = useCallback(
    async (info) => {
      const { file } = info;
      if (file.status !== 'done') return;
      if (!selectedOrg) {
        message.warning('Pick the target IT Glue org first.');
        return;
      }

      setStatus('extracting');
      setError(null);
      setExtracted(null);
      setProposal(null);
      setWriteResult(null);
      setPipelineId(null);
      setFolderIds(null);
      setCachedAt(null);

      const raw = file.originFileObj || file;
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const b64 = (e.target.result || '').toString().split(',')[1];
          const ext = await extractPdfMarkdown(b64, raw.name);
          setExtracted(ext);
          setPdfMeta({ filename: raw.name, sizeBytes: raw.size });

          // ── Step 1b: orchestrator prepare (org resolve + folder bootstrap) ──
          setStatus('preparing');
          const prepResult = await prepareOnboarding({
            orgName: selectedOrg.name,
            userId,
            pdfFilename: raw.name,
            pdfSizeBytes: raw.size,
            sourceMarkdown: ext.markdown,
          });
          setPipelineId(prepResult.pipeline_id);
          setFolderIds(prepResult.folder_ids);
          setPrepareSteps(prepResult.steps || []);
          setPrepareFollowUps(prepResult.manual_follow_ups || []);

          // ── Step 2: server-side multi-pass extraction (Phase O7M) ──
          // The orchestrator runs 6 focused agent passes and stitches
          // the per-pass JSON into the same proposal shape the wizard
          // previously got from the (since removed) single-pass browser
          // SSE flow. Delegates to runExtraction (above) — same call, same
          // status machine as a forced re-scan.
          await runExtraction({
            orgId: selectedOrg.id,
            orgName: selectedOrg.name,
            markdown: ext.markdown,
            folderIds: prepResult.folder_ids,
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            setStatus('idle');
            return;
          }
          setError(err.message);
          setStatus('failed');
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setStatus('failed');
      };
      reader.readAsDataURL(raw);
    },
    [selectedOrg, userId, runExtraction],
  );

  // ─── Visible scan reuse: "Re-scan with AI" (ReuseBanner) ────────────────
  // Forces a fresh, AI-token-spending extraction over the SAME markdown +
  // folderIds already in state, bypassing the cache hit that produced the
  // current proposal. Goes through the identical runExtraction status
  // machine as the first extract — the wizard remounts via wizardKey once
  // the new proposal lands (see runExtraction's setWizardKey call).
  const handleRescan = useCallback(() => {
    if (!selectedOrg || !extracted?.markdown) return;
    runExtraction({
      orgId: selectedOrg.id,
      orgName: selectedOrg.name,
      markdown: extracted.markdown,
      folderIds: folderIds || {},
      forceRefresh: true,
    });
  }, [selectedOrg, extracted, folderIds, runExtraction]);

  // ─── Step 3: commit the wizard's payload ─────────────────────────────────
  // Task 12 (2026-08-05): OnboardingWizard (features/onboarding/OnboardingWizard.jsx)
  // now owns ALL tick-state internally and calls this handler — passed as its
  // `onCommit` prop — only from its own Confirm step, with
  // `{proposal, excluded}`. `proposal` is the FULL, unfiltered object the
  // wizard was given (same reference it received via `extractResult.proposal`
  // — never renumbered); `excluded` is `{prerequisites: number[], dependents:
  // number[]}`, the ORIGINAL bucket indices of every row left unticked (Task
  // 4c contract — see commitOnboarding's docstring for why a client-side
  // `.filter()` must never happen, and OnboardingWizard's own header comment
  // for how it derives `excluded` per-section via computeExcluded). This
  // handler only overlays orgId/quickNotes and forwards to the orchestrator —
  // unchanged from the pre-wizard version below except that the tick-state ->
  // excluded computation now happens inside the wizard, not here.
  const handleConfirmWrite = useCallback(async ({ proposal: wizardProposal, excluded }) => {
    if (!wizardProposal || !selectedOrg || !pipelineId) return;
    // I1 belt-and-braces: the wizard's own `committing` guard (loading +
    // disabled Commit button, plus its internal short-circuit) is the
    // primary defence against a double-submit, but this handler is the
    // one place that actually kicks off the write — never trust a single
    // layer for something that creates duplicate IT Glue records.
    if (status === 'writing') return;
    setStatus('writing');
    setError(null);
    try {
      const allPrerequisites = wizardProposal.prerequisites || [];
      const allDependents = wizardProposal.dependents || [];
      const excludedPrereq = excluded?.prerequisites || [];
      const excludedDependent = excluded?.dependents || [];
      const totalKept = (allPrerequisites.length - excludedPrereq.length)
        + (allDependents.length - excludedDependent.length);
      if (totalKept === 0 && !quickNotesEdited) {
        message.warning('Nothing selected to write.');
        setStatus('preview_ready');
        return;
      }
      const fullProposal = {
        ...wizardProposal,
        orgId: selectedOrg.id,
        quickNotes: quickNotesEdited,
      };
      const result = await commitOnboarding({
        pipelineId,
        proposal: fullProposal,
        excluded: { prerequisites: excludedPrereq, dependents: excludedDependent },
        userId,
      });
      setWriteResult(result);
      setStatus('completed');
    } catch (err) {
      setError(err.message);
      setStatus('failed');
    }
  }, [selectedOrg, pipelineId, quickNotesEdited, userId, status]);

  // ─── Reset ──────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStatus('idle');
    setError(null);
    setExtracted(null);
    setPdfMeta(null);
    setPipelineId(null);
    setFolderIds(null);
    setPrepareSteps([]);
    setPrepareFollowUps([]);
    setProposal(null);
    setRawAgentText('');
    setSchemas({});
    setValidation([]);
    setExtractFollowUps([]);
    setQuickNotesEdited('');
    setCachedAt(null);
    setWizardKey(0);
    setWriteResult(null);
  }, []);

  // ─── Walk-round template: download + re-upload ─────────────────────────
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const { filename, pdf_base64 } = await generateTemplate(pipelineId, proposal, userId);
      downloadBase64Pdf(filename, pdf_base64);
    } catch (e) {
      message.error('Could not generate the walk-round template.');
    }
  }, [pipelineId, proposal, userId]);

  const handleUploadFilled = useCallback(async (file) => {
    if (file.size > 25 * 1024 * 1024) { message.error('That PDF is too large (max 25 MB).'); return false; }
    try {
      const pdfBase64 = await fileToBase64(file);
      const { proposal: corrected, warnings } = await parseFilledTemplate(pipelineId, pdfBase64, userId);
      setProposal(corrected);
      // I2: parse_template REBUILDS both proposal arrays (regrouped, rows
      // dropped, spares appended) — but `validation` verdicts are keyed to
      // the OLD {bucket,index} positions from the original extraction, so
      // reusing them here would flag (or silently default-untick) the
      // WRONG rows against the new array. Degrade rather than mislead, same
      // rule as the failed-schema-fetch path (validate_proposal: "No
      // schemas at all means the fetch failed; degrade rather than flag
      // everything"). `schemas` (type-id-keyed, unaffected by regrouping)
      // and `extractFollowUps` (also unaffected) both stay valid and are
      // deliberately NOT cleared here.
      setValidation([]);
      setQuickNotesEdited(corrected.quickNotes || '');
      setWizardKey((k) => k + 1);
      if (warnings?.length) message.warning(warnings.join(' '));
      message.success('Template parsed — review the updated proposal, then Commit.');
    } catch (e) {
      const reason = e?.response?.data?.detail?.reason;
      if (reason === 'no_form_fields') {
        message.error(
          'That PDF was flattened. Re-open the template in Adobe Reader or Edge, fill it, ' +
          'and use Save (not Print). On iPad use the Adobe Acrobat Reader app.'
        );
      } else if (reason === 'pipeline_mismatch' || reason === 'form_version_mismatch') {
        message.error('This PDF does not match the current onboarding run. Download a fresh template.');
      } else {
        message.error('Could not parse the filled template.');
      }
    }
    return false; // prevent Upload's default POST
  }, [pipelineId, proposal, userId]);

  const stepIndex = useMemo(() => {
    if (status === 'completed') return 3;
    if (status === 'writing' || status === 'preview_ready') return 2;
    if (status === 'extracting' || status === 'preparing' || status === 'analysing') return 1;
    if (selectedOrg) return 1;
    return 0;
  }, [status, selectedOrg]);

  // ReuseBanner's record count — same definition the rest of the wizard
  // uses for "everything the extractor proposed" (both buckets, unfiltered).
  const recordCount = (proposal?.prerequisites?.length || 0) + (proposal?.dependents?.length || 0);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <PageContainer>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 4 }}>
          <FileAddOutlined style={{ marginRight: 10 }} />
          Customer Onboarding
        </Title>
        <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 13 }}>
          Ingest an IT-handover PDF into IT Glue. Sonnet 4.6 parses the document, you review the
          proposal, the dashboard writes only what you confirm.
        </Text>

        <Steps
          current={stepIndex}
          style={{ marginTop: 24, marginBottom: 24 }}
          items={[
            { title: 'Pick org' },
            { title: 'Upload PDF' },
            { title: 'Review proposal' },
            { title: 'Done' },
          ]}
        />

        {error && (
          <Alert
            type="error"
            showIcon
            message="Onboarding failed"
            description={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]}>
          {/* ── Left column: org + upload + status ──────────────────── */}
          <Col xs={24} lg={10}>
            <SectionCard title={<span><ApiOutlined style={{ marginRight: 8 }} />Step 1 — Target IT Glue org</span>}>
              <Select
                showSearch
                placeholder="Type at least 2 characters of the company name"
                style={{ width: '100%' }}
                value={selectedOrg?.id}
                onSearch={setOrgQuery}
                filterOption={false}
                loading={orgLoading}
                notFoundContent={orgLoading ? <Spin size="small" /> : (orgQuery.length < 2 ? 'Type to search' : 'No matches')}
                options={orgOptions}
                onChange={(value, opt) => setSelectedOrg(value ? { id: value, name: opt.label } : null)}
                disabled={status !== 'idle' && status !== 'failed'}
                allowClear
              />
              {selectedOrg && (
                <StatusLine style={{ marginTop: 12 }}>
                  <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
                  Selected: <Tag>{selectedOrg.name}</Tag>
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}>id={selectedOrg.id}</Text>
                </StatusLine>
              )}
            </SectionCard>

            <SectionCard title={<span><InboxOutlined style={{ marginRight: 8 }} />Step 2 — Upload IT-handover PDF</span>}>
              <Dragger
                accept=".pdf"
                multiple={false}
                showUploadList={false}
                customRequest={({ onSuccess }) => setTimeout(() => onSuccess('ok'), 0)}
                onChange={handleUpload}
                disabled={!selectedOrg || ['extracting', 'preparing', 'analysing', 'writing'].includes(status)}
              >
                <p className="ant-upload-drag-icon" style={{ color: 'var(--odin-accent)' }}>
                  <InboxOutlined style={{ fontSize: 36 }} />
                </p>
                <Text style={{ color: 'var(--odin-text-primary)' }}>Click or drag a single PDF here</Text>
                <Paragraph style={{ color: 'var(--odin-text-tertiary)', fontSize: 12, marginTop: 8 }}>
                  Max 25 MB / 200 pages. Scanned PDFs (no text layer) will be rejected — OCR is v2.
                </Paragraph>
              </Dragger>

              {extracted && (
                <StatusLine style={{ marginTop: 12 }}>
                  <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
                  Extracted {extracted.pages} pages ({(extracted.markdown || '').length.toLocaleString()} chars markdown)
                </StatusLine>
              )}

              {(status === 'extracting' || status === 'preparing' || status === 'analysing') && (() => {
                const summary = status === 'analysing' ? summariseExtractProgress(extractProgress) : null;
                return (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={status === 'extracting' ? 25 : status === 'preparing' ? 50 : summary.percent}
                      status="active"
                      strokeColor="var(--odin-accent)"
                      showInfo={false}
                    />
                    <StatusLine style={{ marginTop: 8 }}>
                      <Spin size="small" />
                      {status === 'extracting' && 'Docling extracting PDF…'}
                      {status === 'preparing' && 'Orchestrator: resolving org + bootstrapping default folders…'}
                      {status === 'analysing' && summary.caption}
                    </StatusLine>
                  </div>
                );
              })()}

              {/* Phase O5 — folder bootstrap audit (after prepare returns) */}
              {folderIds && status !== 'idle' && status !== 'failed' && (
                <div style={{ marginTop: 12, padding: 10, background: 'var(--odin-surface-hover)', borderRadius: 6 }}>
                  <Text style={{ fontSize: 11, color: 'var(--odin-text-tertiary)', display: 'block', marginBottom: 6 }}>
                    <FolderOpenOutlined style={{ marginRight: 6 }} />
                    Folder bootstrap (org: {selectedOrg?.name})
                  </Text>
                  {prepareSteps.map((s, i) => (
                    <Text key={i} style={{ fontSize: 11, color: 'var(--odin-text-tertiary)', display: 'block' }}>
                      • {s.step}: {s.status === 'ok' ? '✓' : '✗'} {Array.isArray(s.created) && s.created.length > 0 && `created ${s.created.length}`} {Array.isArray(s.reused) && s.reused.length > 0 && `reused ${s.reused.length}`}
                    </Text>
                  ))}
                  {prepareFollowUps.map((f, i) => (
                    <Text key={`fu-${i}`} style={{ fontSize: 11, color: 'var(--odin-warning)', display: 'block', marginTop: 4 }}>
                      <WarningOutlined style={{ marginRight: 4 }} />
                      {f}
                    </Text>
                  ))}
                </div>
              )}
            </SectionCard>
          </Col>

          {/* ── Right column: preview + confirm + result ───────────── */}
          <Col xs={24} lg={14}>
            {!proposal && status !== 'completed' && (
              <SectionCard title="Proposed records">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<Text style={{ color: 'var(--odin-text-tertiary)' }}>Upload a PDF to see the agent's proposal here.</Text>}
                />
              </SectionCard>
            )}

            {proposal && status !== 'completed' && (
              <>
                {proposal.summary && (
                  <SectionCard title="Agent summary">
                    <Paragraph style={{ color: 'var(--odin-text-primary)' }}>{proposal.summary}</Paragraph>
                  </SectionCard>
                )}

                {/* Walk-round template controls — download the pre-filled PDF, fill it on site,
                    re-upload to merge the engineer's corrections back into the proposal */}
                <SectionCard title="Walk-round template (optional)">
                  <Text style={{ color: 'var(--odin-text-secondary)', fontSize: 13, display: 'block', marginBottom: 16 }}>
                    Download a pre-populated fillable PDF, correct it on-site with the engineer, then
                    re-upload to update this proposal before committing to IT Glue.
                  </Text>
                  <Space wrap>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadTemplate}
                      disabled={!pipelineId}
                    >
                      Download walk-round template
                    </Button>
                    <Upload
                      accept="application/pdf"
                      beforeUpload={handleUploadFilled}
                      showUploadList={false}
                    >
                      <Button icon={<UploadOutlined />} disabled={!pipelineId}>
                        Upload filled template
                      </Button>
                    </Upload>
                  </Space>
                </SectionCard>

                {/* Quick Notes editor — operator can revise the agent-synthesised summary before commit */}
                <SectionCard title={<span>Quick Notes <Tag color="cyan">editable</Tag></span>}>
                  <TextArea
                    rows={5}
                    value={quickNotesEdited}
                    onChange={(e) => setQuickNotesEdited(e.target.value)}
                    placeholder="1-3 paragraph operator-readable summary that will land in IT Glue's org-level Quick Notes."
                    style={{
                      background: 'var(--odin-code-bg)',
                      color: 'var(--odin-text-primary)',
                      border: '1px solid var(--odin-border)',
                    }}
                  />
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 11, marginTop: 6, display: 'block' }}>
                    Lands at the org root via update_organization on commit. Faucets sample (§2.2) is ~3 sentences covering
                    identity / endpoint security / network shape / backup posture / operationally-surprising bits.
                  </Text>
                </SectionCard>

                {/* Blocker warnings (Email, etc.) */}
                {proposal.blockerWarnings?.length > 0 && (
                  <SectionCard title="Blocker warnings — operator follow-up needed">
                    <Alert
                      type="warning"
                      showIcon
                      message={`${proposal.blockerWarnings.length} item${proposal.blockerWarnings.length === 1 ? '' : 's'} could not be written by the pipeline`}
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {proposal.blockerWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      }
                    />
                  </SectionCard>
                )}

                {/* Duplicate detection */}
                {proposal.duplicateWarnings?.length > 0 && (
                  <SectionCard title="Duplicates detected — not re-proposed">
                    <Alert
                      type="info"
                      showIcon
                      message={`${proposal.duplicateWarnings.length} record${proposal.duplicateWarnings.length === 1 ? '' : 's'} already exist at this org`}
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {proposal.duplicateWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      }
                    />
                  </SectionCard>
                )}

                {/* Proposal notes (Select substitutions, etc.) */}
                {proposal.proposalNotes?.length > 0 && (
                  <SectionCard title="Agent decisions (review)">
                    <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--odin-text-secondary)' }}>
                      {proposal.proposalNotes.map((n, i) => <li key={i} style={{ fontSize: 12 }}>{n}</li>)}
                    </ul>
                  </SectionCard>
                )}

                {/* Review wizard (Task 12) — sequential per-section review
                    (Locations/Contacts/Configurations/Passwords/Flex-assets)
                    ending in its own Confirm step; the wizard owns all
                    tick-state and is the ONLY place commit is offered, so
                    there is no separate commit button on this page. */}
                <SectionCard title="Review proposal">
                  <Text style={{ color: 'var(--odin-text-tertiary)', fontSize: 12, display: 'block', marginBottom: 12 }}>
                    <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                    Writes to <strong>{selectedOrg?.name}</strong> (id {selectedOrg?.id}) via the orchestrator. Two waves
                    (prereqs → dependents), then auto-vault Wireless passwords are filed under Network &amp; Internet, then
                    Quick Notes lands on the org. Password values are sent only on commit and are NOT echoed back.
                  </Text>
                  {/* Visible scan reuse: shows only when this proposal came from the
                      extraction cache (cachedAt truthy) — see runExtraction/handleRescan
                      above. Review step only, matching this SectionCard's own scope. */}
                  <ReuseBanner
                    cachedAt={cachedAt}
                    recordCount={recordCount}
                    onRescan={handleRescan}
                    rescanning={status === 'analysing'}
                  />
                  <OnboardingWizard
                    key={wizardKey}
                    extractResult={{ proposal, schemas, validation, follow_ups: extractFollowUps }}
                    onCommit={handleConfirmWrite}
                    committing={status === 'writing'}
                  />
                  <Divider style={{ borderColor: 'var(--odin-border)' }} />
                  <Button onClick={handleReset} icon={<ReloadOutlined />}>
                    Start over
                  </Button>
                </SectionCard>
              </>
            )}

            {status === 'completed' && writeResult && (
              <SectionCard
                title={
                  <span>
                    {writeResult.success ? (
                      <CheckCircleOutlined style={{ color: 'var(--odin-success)', marginRight: 8 }} />
                    ) : (
                      <ExclamationCircleOutlined style={{ color: 'var(--odin-warning)', marginRight: 8 }} />
                    )}
                    Onboarding {writeResult.success ? 'committed' : 'failed mid-flight'}
                  </span>
                }
              >
                {writeResult.failure_step && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Failed at step: ${writeResult.failure_step}`}
                    description={writeResult.failure_detail}
                    style={{ marginBottom: 16 }}
                  />
                )}
                {writeResult.rollback_hint && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Rollback hint"
                    description={writeResult.rollback_hint}
                    style={{ marginBottom: 16 }}
                  />
                )}
                {writeResult.blocker_warnings?.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`${writeResult.blocker_warnings.length} blocker warning${writeResult.blocker_warnings.length === 1 ? '' : 's'} — operator follow-up`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {writeResult.blocker_warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    }
                    style={{ marginBottom: 16 }}
                  />
                )}
                {writeResult.manual_follow_ups?.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`${writeResult.manual_follow_ups.length} manual follow-up${writeResult.manual_follow_ups.length === 1 ? '' : 's'}`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {writeResult.manual_follow_ups.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    }
                    style={{ marginBottom: 16 }}
                  />
                )}
                {/* Success/failure totals, per-item failure detail, and the
                    NOT-created-password follow-ups panel — all superseded
                    from the old inline Alerts above by ResultsStep +
                    FollowUpsPanel (Tasks 9-11). blocker_warnings and
                    manual_follow_ups stay as their own Alerts above:
                    ResultsStep doesn't render those. */}
                <ResultsStep
                  result={{
                    successful_records: writeResult.successful_ids?.length || 0,
                    failed_records: writeResult.failed_items?.length || 0,
                    failed_items: writeResult.failed_items || [],
                    follow_ups: writeResult.follow_ups || [],
                  }}
                />

                {/* Per-step audit */}
                {writeResult.steps?.length > 0 && (
                  <div style={{ marginBottom: 16, padding: 10, background: 'var(--odin-surface-hover)', borderRadius: 6 }}>
                    <Text style={{ fontSize: 11, color: 'var(--odin-text-tertiary)', display: 'block', marginBottom: 6 }}>
                      Per-step audit
                    </Text>
                    {writeResult.steps.map((s, i) => (
                      <Text key={i} style={{ fontSize: 11, color: 'var(--odin-text-secondary)', display: 'block' }}>
                        • {s.step}: {s.status}{s.count !== undefined && ` (${s.count} items)`}{s.filed_count !== undefined && ` (filed ${s.filed_count})`}{s.error && ` — ${s.error}`}
                      </Text>
                    ))}
                  </div>
                )}

                <Table
                  size="small"
                  columns={[
                    { title: 'Tool', dataIndex: 'tool', key: 'tool', width: 200,
                      render: (t) => <Tag color="blue">{TOOL_LABELS[t] || t}</Tag>,
                    },
                    {
                      title: 'Result',
                      key: 'result',
                      render: () => <Tag color="success">created</Tag>,
                      width: 100,
                    },
                    {
                      title: 'IT Glue URL',
                      key: 'url',
                      render: (_, r) =>
                        r.url ? <a href={safeHref(r.url)} target="_blank" rel="noopener noreferrer">{r.url}</a> : '—',
                    },
                  ]}
                  dataSource={(writeResult.successful_ids || []).map((r, i) => ({ ...r, key: i }))}
                  pagination={false}
                />
                <Divider style={{ borderColor: 'var(--odin-border)' }} />
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  Onboard another customer
                </Button>
              </SectionCard>
            )}
          </Col>
        </Row>
      </PageContainer>
    </DashboardLayout>
  );
}

export default CustomerOnboardingPage;
